import { Clipboard } from '@angular/cdk/clipboard';
import { Overlay } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import pako from 'pako';
import { finalize, from, Observable, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { Language, LanguageService } from '../../../../../core/i18n';
import {
  ATTACHMENTS_TYPES,
  activeTranslations,
  CACHE_NAMES,
  CHAT_EXTERNAL_TYPE,
  CHAT_SUB_TYPES,
  DEFAULT_ERROR,
  ERROR_MESSAGES,
  EXTERNAL_ID_PREFIX,
  INST_REACTION_ACTION,
  MESSAGE_STATUS,
  MESSAGE_TYPES,
  SEND_ACTION_TYPE,
  TRANSCRIPTION_STATUS
} from '../../../../../enum/constants';
import { formatFullName, getColorByLetter } from '../../../../../helpers';
import { getExternalIDPrefixType } from '../../../../../helpers/utils';
import { plannerExtraActionContextMap } from '../../../../../interfaces/planner-table.interface';
import { BaseService } from '../../../../../services/base.service';
import { CacheService } from '../../../../../services/cache.service';
import { ChatService } from '../../../../../services/chat.service';
import { FileSaveService } from '../../../../../services/file-save.service';
import { MessagesTemplatesService } from '../../../../../services/messages-templates.service';
import { ModalService } from '../../../../../services/modal.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { PlannerActionService } from '../../../../../services/planner-action.service';
import { getFileViewerType } from '../../../../file-viewer/file-viewer.constants';
import { IPromProduct } from '../../../../prom/prom.interface';
import { TReaction } from '../../../../reaction/reaction.interface';
import { ISendMessageBody } from '../chat/chat.interface';
import {
  Attachment,
  AVATAR_REACTIONS_EXTERNAL_TYPES,
  attachmentsTooltipMap,
  BTN_ACTIONS,
  editMessageTypes,
  IMenuMessage,
  InstSharedPost,
  Message,
  MessageData,
  MessageForwardedFrom,
  MessageLog,
  MessageReactions,
  MessageReactionsEmoji,
  MessageReplyTo,
  MessageSender,
  MessageTranscription,
  MessageTranslation,
  RELOAD_MEDIA_TYPE_CHATS,
  RIA_MESSAGE_TYPES_ACTION,
  replyMessageTypes,
  riaAuctionMessageTypes,
  riaMessageTypes,
  riaMessageTypesNumbersMap,
  riaTradeAcceptMessageTypes,
  riaTradeDeclineMessageTypes,
  riaTradeMessageTypes,
  riaTradePhotoMessageTypes,
} from './chat-message.interface';

type CopyMenuMode = 'selected' | 'all';

/** Мова інтерфейсу → ISO-код мови перекладу для бекенду. */
const TRANSLATION_LANGUAGE_CODES: Record<Language, string> = {
  ua: 'uk',
  en: 'en',
};

@Component({
  selector: 'app-chat-message',
  templateUrl: './chat-message.component.html',
  styleUrl: './chat-message.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatMessageComponent implements OnDestroy {
  @Input() isPinned!: boolean;
  @Input() isDelayed!: boolean;
  @Output() onEditMessage = new EventEmitter();
  @Output() onSendMessage = new EventEmitter();
  @Output() onReply = new EventEmitter();
  @ViewChild('menu') menuTemplate!: TemplateRef<any>;

  // посилання на елемент із текстом повідомлення (для перевірки меж виділення)
  @ViewChild('messageTextEl', { static: false }) messageTextRef?: ElementRef<HTMLElement>;

  private readonly destroy$: Subject<void> = new Subject<void>();
  // private overlayRef!: OverlayRef;
  private selectedText = '';
  private _message!: Message;
  protected readonly markdownChatTypes: CHAT_EXTERNAL_TYPE[] = [
    CHAT_EXTERNAL_TYPE.TELEGRAM_BOT,
    CHAT_EXTERNAL_TYPE.TELEGRAM_USER,
  ];
  protected menus: IMenuMessage[] = [];
  protected data: MessageData = {};
  protected commentedPostData?: InstSharedPost;
  protected isSharedPost: boolean = false;
  protected productData?: IPromProduct;
  protected media: Attachment[] = [];
  protected files: Attachment[] = [];
  protected audio: Attachment[] = [];
  protected posts: Attachment[] = [];
  protected round?: Attachment;
  protected sticker?: Attachment;
  protected unsupportedMessage: boolean = false;
  protected sender?: MessageSender;
  protected logs: MessageLog[] = [];
  protected reactions?: MessageReactions;
  protected isLogsVisible: boolean = false;
  protected forwardedFrom?: MessageForwardedFrom;
  protected replyTo?: MessageReplyTo;
  protected isTranslating: boolean = false;
  protected isTranslationVisible: boolean = true;
  protected readonly TRANSCRIPTION_STATUS = TRANSCRIPTION_STATUS;

  @Input()
  set message(value: Message) {
    this._message = value;
    this.initData();
    this.initAttachments();
    this.initSender();
    this.initForwardedFrom();
    this.initReplyTo();
    this.initLogs();
    this.initReactions();
    this.initMenu();
    this.initSharedPost();
    this.initProductData();
  }

  get message(): Message {
    return this._message;
  }

  constructor(
    private readonly messagesTemplatesService: MessagesTemplatesService,
    private readonly clipboard: Clipboard,
    private readonly notificationService: NotificationService,
    private readonly plannerActionService: PlannerActionService,
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly cacheService: CacheService,
    private readonly fileSaveService: FileSaveService,
    private readonly translateService: TranslateService,
    private readonly modalService: ModalService,
    protected readonly chatService: ChatService,
    protected readonly baseService: BaseService,
    private readonly languageService: LanguageService,
  ) {}

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Ім'я менеджера, який відправив повідомлення з CRM. */
  protected get createdByUserName(): string {
    const user = this.message.created_by_user;
    if (!user) return '';

    return formatFullName(user.first_name, user.last_name) || (user.email ?? '');
  }

  protected get createdByUserAvatarUrl(): string {
    const avatarUrl = this.message.created_by_user?.avatar_url;

    return avatarUrl || '';
  }

  protected get transcription(): MessageTranscription | undefined {
    return this.message.x_data?.transcription;
  }

  /** Переклад повідомлення на поточну мову інтерфейсу. */
  protected get translation(): MessageTranslation | undefined {
    return this.message.x_data?.translations?.[this.translationLanguage];
  }

  private get translationLanguage(): string {
    return TRANSLATION_LANGUAGE_CODES[this.languageService.current];
  }

  private get hasTranscribableMedia(): boolean {
    return !!this.audio.length || !!this.round || this.media.some((item) => item.type === ATTACHMENTS_TYPES.VIDEO);
  }

  private get translatableSource(): MessageTranslation['source'] | undefined {
    if (this.message.text?.trim()) return 'text';
    if (this.transcription?.status === TRANSCRIPTION_STATUS.DONE && this.transcription.text?.trim()) {
      return 'transcription';
    }
    return undefined;
  }

  private initLogs() {
    this.logs = this.message.x_data?.logs ?? [];
  }

  private initReactions() {
    this.reactions = this.message.x_data?.reaction;

    if (this.reactions?.emoji) {
      this.reactions.emojis = [
        {
          key: this.reactions.emoji,
          emoji: this.reactions.emoji,
          count: 1,
          users: [],
        },
      ];
    }
  }

  private initSender() {
    this.sender = this.message.x_data?.sender;

    if (!this.sender) return;

    this.sender.color = getColorByLetter(this.sender.name);
  }

  private initData(): void {
    this.unsupportedMessage = JSON.stringify(this.message).includes('is_unsupported');

    if (this.message.type_message == MESSAGE_TYPES.TEXT_COMMENT) {
      this.initCommentOnPost();
    }

    if (!riaMessageTypes.includes(this.message.type_message) || !this.message.x_data?.text) return;

    try {
      this.data = JSON.parse(this.message.x_data.text);
    } catch {}
  }

  private initForwardedFrom() {
    this.forwardedFrom = this.message.x_data?.fwd_from;
  }

  private initReplyTo() {
    this.replyTo = this.message.x_data?.reply_to;
    if (!this.message.x_data?.tiktok?.comment_id) return;
    if (!this.message.created_by) {
      this.replyTo = undefined;
      return;
    }

    const commentId = this.message.x_data.tiktok.comment_id;
    const replyTarget = this.chatService.chatMessagesInfo.items.find((item) => {
      return item.x_data?.value?.id === commentId || item.x_data?.tiktok?.comment_id === commentId;
    });

    if (!replyTarget) return;

    const replyTargetText = replyTarget.x_data?.value?.text || replyTarget.x_data?.text || replyTarget.text || '';
    const replyTargetName = replyTarget.x_data?.sender?.name;

    if (this.replyTo) {
      if (!this.replyTo.to_msg_id) {
        this.replyTo.to_msg_id = replyTarget.id;
      }
      if (!this.replyTo.to_msg_text) {
        this.replyTo.to_msg_text = replyTargetText;
      }
      if (!this.replyTo.to_name && replyTargetName) {
        this.replyTo.to_name = replyTargetName;
      }
      return;
    }

    this.replyTo = {
      to_msg_id: replyTarget.id,
      to_msg_text: replyTargetText,
      to_name: replyTargetName,
    };
  }

  private initCommentOnPost() {
    const post = this.message?.x_data?.post;

    if (post) {
      this.commentedPostData = post;
      this.commentedPostData.attachments = this.mapCommentedInstPostToAttachments(this.commentedPostData);
    }
  }

  private initSharedPost() {
    const attachments = this.message.attachments?.array;
    if (!attachments || !JSON.stringify(attachments).includes('postUrl')) return;

    this.isSharedPost = true;
  }

  protected hasRenderableAttachments(items: Attachment[] = []): boolean {
    return items.some((item) => !!(item.shown_url || item.url || item.id));
  }

  protected get sharedPostItems(): Attachment[] {
    return this.posts.length ? this.posts : this.media;
  }

  protected get sharedPost(): Attachment | undefined {
    return this.sharedPostItems[0];
  }

  private initProductData() {
    const product = this.message?.x_data?.product;
    if (product) {
      this.productData = product;
    }
  }

  private signature(a: Attachment): string {
    return `${a.url ?? a.id ?? ''}__${a.original_id ?? ''}`;
  }

  private initAttachments() {
    const prev = [...this.media, ...this.files, ...this.audio, this.round, this.sticker].filter(
      Boolean,
    ) as Attachment[];

    const prevByKey = new Map<string, Attachment>();
    prev.forEach((p) => prevByKey.set(this.signature(p), p));

    this.resetAttachments();

    this.message.attachments?.array?.forEach((attachment: Attachment) => {
      const sig = this.signature(attachment);
      const same = prevByKey.get(sig);

      if (same?.shown_url && !same.error) {
        attachment.shown_url = same.shown_url;
        attachment.error = same.error;
        attachment.loading = false;
      }

      switch (attachment.type) {
        case ATTACHMENTS_TYPES.IMAGE:
        case ATTACHMENTS_TYPES.VIDEO:
          this.initMedia(attachment);
          break;

        case ATTACHMENTS_TYPES.AUDIO:
          this.initAudio(attachment);
          break;

        case ATTACHMENTS_TYPES.ROUND:
          this.initRound(attachment);
          break;

        case ATTACHMENTS_TYPES.STICKER:
          this.initSticker(attachment);
          break;

        case ATTACHMENTS_TYPES.DOCUMENT:
          this.files.push(attachment);
          break;

        case ATTACHMENTS_TYPES.POST:
          this.posts.push(attachment);
          break;

        case ATTACHMENTS_TYPES.UNKNOWN:
        default:
          this.unsupportedMessage = true;
          break;
      }
    });

    prev.forEach((p) => {
      const stillUsed = this.signature(p) && this.findAttachmentBySignature(this.signature(p));
      if (!stillUsed && p.shown_url?.startsWith('blob:')) {
        URL.revokeObjectURL(p.shown_url);
      }
    });
  }

  private findAttachmentBySignature(sig: string): Attachment | undefined {
    const all = [...this.media, ...this.files, ...this.audio, this.round, this.sticker].filter(Boolean) as Attachment[];
    return all.find((a) => this.signature(a) === sig);
  }

  private initMedia(attachment: Attachment) {
    this.media.push(attachment);
    this.initMediaShownUrl(attachment);

    // attachment.loading = true;
    // const cacheKey = `/${this.message.chat_id}/${attachment.url || attachment.original_id || attachment.id}`;
    //
    // from(caches.open(CHAT_MEDIA_CACHE_NAME)).pipe(
    //   switchMap(cache => from(cache.match(cacheKey))),
    //   takeUntil(this.destroy$),
    // ).subscribe({
    //   next: (cachedResponse) => {
    //     if (cachedResponse) {
    //       this.initMediaShownUrl(attachment);
    //       return;
    //     }
    //     attachment.loading = false;
    //   },
    //   error: () => {
    //     attachment.loading = false;
    //   }
    // });
  }

  private initAudio(attachment: Attachment) {
    this.audio.push(attachment);

    if (attachment.voice) this.initMediaShownUrl(attachment);
  }

  private initRound(attachment: Attachment) {
    this.round = attachment;
    this.initMediaShownUrl(this.round);
  }

  private initSticker(attachment: Attachment) {
    this.sticker = attachment;
    this.initMediaShownUrl(this.sticker);
  }

  protected initMediaShownUrl(attachment: Attachment) {
    if (attachment.shown_url) return;

    this.fetchAndPrepareAttachment(attachment, true);
  }

  private fetchAndPrepareAttachment(attachment: Attachment, isForDisplay: boolean, onSuccess?: (blob: Blob) => void) {
    if (!attachment.url && !attachment.id && !attachment.key) return;

    attachment.loading = true;
    if (attachment.key && !attachment.url) {
      attachment.url = `${environment.baseURL}/assets/download?key=${attachment.key}`;
    }

    const cacheKey = `/${this.message.chat_id}/${attachment.url || attachment.original_id || attachment.id}`;
    let fetch$: Observable<Blob>;

    if (attachment.url) {
      fetch$ = this.fileSaveService.downloadMediaFromUrl(attachment.url, this.chatService.chat?.channel_id);
    } else if (attachment.id) {
      fetch$ = this.chatService.downloadMedia(this.message.chat_id, attachment.id);
    }

    this.cacheService
      .getCachedOrFetchBlob(cacheKey, fetch$!, CACHE_NAMES.CHAT_MEDIA)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (attachment.loading = false)),
      )
      .subscribe({
        next: (blob) => {
          if (!blob) return;

          const mimetype = attachment.mime_type || attachment.mimeType;

          if (isForDisplay && mimetype?.includes('tgsticker')) {
            this.convertTgsToJson(attachment, blob);
            return;
          }
          const blobForUrl = mimetype ? new Blob([blob], { type: mimetype }) : blob;
          attachment.shown_url = URL.createObjectURL(blobForUrl);

          if (onSuccess) onSuccess(blob);
        },
        error: () => {
          attachment.error = true;
        },
      });
  }

  private convertTgsToJson(attachment: Attachment, tgsBlob: Blob) {
    attachment.loading = true;
    from(tgsBlob.arrayBuffer())
      .pipe(finalize(() => (attachment.loading = false)))
      .subscribe({
        next: (arrayBuffer) => {
          const decompressed = pako.inflate(arrayBuffer, { to: 'string' });
          attachment.shown_url = JSON.parse(decompressed);
        },
      });
  }

  private resetAttachments(): void {
    this.media = [];
    this.files = [];
    this.audio = [];
    this.posts = [];
    this.round = undefined;
    this.sticker = undefined;
    this.unsupportedMessage = false;
  }

  protected onBtnAction(action: BTN_ACTIONS) {
    this.message.type_message = RIA_MESSAGE_TYPES_ACTION[action] as unknown as MESSAGE_TYPES;

    const payload: ISendMessageBody = {
      text: this.message.x_data?.text || '{}',
      send_action: SEND_ACTION_TYPE.SEND,
      x_data: {
        ria_type_mes: riaMessageTypesNumbersMap[action],
        ria_previous_message_id: this.message.x_data?.id || 0,
      },
    };
    this.chatService.sendMessageAction(this.chatService.chat.id, payload).subscribe({
      next: (message) => {
        this.chatService.addTempMessage(message);
        this.chatService.reloadChatList();
      },
      error: (res: any) => {
        (res.error?.errors || []).forEach((err: any) => {
          this.notificationService.showError(
            activeTranslations.notifications_texts.error.error,
            ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
          );
        });
      },
    });
  }

  private initMenu(copyMode: CopyMenuMode = 'all') {
    this.menus = [];

    const isReplyCommentAvailable =
      [CHAT_EXTERNAL_TYPE.INSTAGRAM, CHAT_EXTERNAL_TYPE.FACEBOOK, CHAT_EXTERNAL_TYPE.TIKTOK].includes(
        this.chatService.chat.external_type,
      ) && this.commentedPostData;

    if (
      isReplyCommentAvailable &&
      this.message.status === MESSAGE_STATUS.SEND &&
      !this.unsupportedMessage &&
      !this.isDelayed
    ) {
      this.menus.push({
        title: 'reply_comment',
        icon: 'reply',
        action: () => this.onReplyMessage(SEND_ACTION_TYPE.REPLY_COMMENT),
      });
    }

    if (
      replyMessageTypes.includes(this.chatService.chat.external_type) &&
      this.chatService.canReplyToMessage(this.message) &&
      !this.isDelayed
    ) {
      this.menus.push({
        title: 'reply',
        icon: 'reply',
        action: () => this.onReplyMessage(SEND_ACTION_TYPE.REPLY),
      });
    }

    if (this.media.length === 1 || this.files.length === 1 || this.audio.length === 1 || this.round || this.sticker) {
      this.menus.push({
        title: 'download',
        icon: 'download',
        action: () => this.onDownloadAttachments(),
      });
    }

    if (this.media.length > 1 || this.files.length > 1) {
      this.menus.push({
        title: 'download_all',
        icon: 'download',
        action: () => this.onDownloadAttachments(),
      });
    }

    if (this.message.created_by) {
      if (this.message.status === MESSAGE_STATUS.FAIL && !this.isDelayed) {
        this.menus.push({ title: 'resend', icon: 'restart_alt', action: () => this.onResend() });
      }

      if (
        (editMessageTypes.includes(this.chatService.chat.external_type) &&
          this.chatService.canEditMessage(this.message)) ||
        (this.isDelayed && !this.message.is_ai)
      ) {
        this.menus.push({
          title: 'change',
          icon: 'edit',
          action: () => this.onEdit(),
        });
      }

      if (
        [CHAT_EXTERNAL_TYPE.OLX].includes(this.chatService.chat.external_type) &&
        this.message.status === MESSAGE_STATUS.SEND &&
        !this.message.is_read &&
        !this.isDelayed
      ) {
        this.menus.push({ title: 'check', icon: 'done_all', action: () => this.onCheckRead() });
      }
    }

    if (!this.isDelayed) {
      if (!this.message.is_pinned) {
        this.menus.push({ title: 'pin', icon: 'pin', is_svg: true, action: () => this.onPin() });
      } else {
        this.menus.push({ title: 'unpin', icon: 'unpin', is_svg: true, action: () => this.onPin() });
      }
    }

    if (!this.isDelayed && !this.message.deleted_at && this.message.status === MESSAGE_STATUS.SEND) {
      const transcriptionStatus = this.transcription?.status;
      if (
        this.hasTranscribableMedia &&
        transcriptionStatus !== TRANSCRIPTION_STATUS.DONE &&
        transcriptionStatus !== TRANSCRIPTION_STATUS.PENDING
      ) {
        this.menus.push({
          title: 'transcribe',
          icon: 'transcribe',
          action: () => this.onTranscribe(transcriptionStatus === TRANSCRIPTION_STATUS.FAILED),
        });
      }

      if (this.translatableSource && !this.isTranslating) {
        if (!this.translation) {
          this.menus.push({ title: 'translate', icon: 'translate', action: () => this.onTranslate() });
        } else {
          this.menus.push({
            title: this.isTranslationVisible ? 'hide_translation' : 'show_translation',
            icon: 'translate',
            action: () => (this.isTranslationVisible = !this.isTranslationVisible),
          });
        }
      }
    }

    if (this.message.text?.trim()) {
      if (copyMode === 'selected') {
        this.menus.push({
          title: 'copy_selected_text',
          icon: 'content_copy',
          action: () => this.onCopySelected(),
        });
      } else {
        this.menus.push({
          title: 'copy',
          icon: 'content_copy',
          action: () => this.onCopyAll(),
        });
      }

      this.menus.push({
        title: 'save_as_a_quick_reply',
        icon: 'bolt',
        action: () => this.onSaveQuickReply(),
      });
    }

    if (this.isDelayed) {
      this.menus.push({ title: 'send_now', icon: 'send', action: () => this.onSendNow() });
      this.menus.push({ title: 'delete', icon: 'delete', action: () => this.onDeleteDelayed() });
      this.menus.push({ title: 'reschedule', icon: 'history', action: () => this.onReschedule() });
    }

    if (!this.isDelayed && this.baseService.user.is_admin) {
      this.menus.push({
        title: 'delete',
        icon: 'delete',
        is_lines: true,
        action: () => this.onDeleteMessage(),
      });
    }
  }

  protected onTranscribe(force = false) {
    const messageId = this.message.id;

    this.chatService
      .transcribeMessage(messageId, force)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (transcription) => {
          this.chatService.patchMessageXData(messageId, (x_data) => ({ ...x_data, transcription }));
        },
        error: (res) => this.showRequestErrors(res),
      });
  }

  private onTranslate() {
    const messageId = this.message.id;
    const source = this.translatableSource;
    if (!source) return;

    this.isTranslating = true;
    this.isTranslationVisible = true;

    this.chatService
      .translateMessage(messageId, this.translationLanguage)
      .pipe(
        finalize(() => (this.isTranslating = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ language, text }) => {
          const translation: MessageTranslation = { text, source, updated_at: new Date().toISOString() };
          this.chatService.patchMessageXData(messageId, (x_data) => ({
            ...x_data,
            translations: { ...(x_data.translations ?? {}), [language]: translation },
          }));
        },
        error: (res) => this.showRequestErrors(res),
      });
  }

  private showRequestErrors(res: any) {
    const errors: { code: string }[] = res?.error?.errors?.length ? res.error.errors : [{ code: '' }];
    errors.forEach((err) => {
      this.notificationService.showError(
        activeTranslations.notifications_texts.error.error,
        ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
      );
    });
  }

  private onDownloadAttachments() {
    const foundAttachments: Attachment[] = [];
    if (this.media.length) foundAttachments.push(...this.media);
    if (this.files.length) foundAttachments.push(...this.files);
    if (this.audio) foundAttachments.push(...this.audio);
    if (this.round) foundAttachments.push(this.round);
    if (this.sticker) foundAttachments.push(this.sticker);

    if (!foundAttachments.length) return;

    foundAttachments.forEach((attachment) => this.onDownloadAttachment(attachment));
  }

  protected onTextSelect(): void {
    const host = this.messageTextRef?.nativeElement || document.getElementById(this.message.id);
    this.selectedText = this.getSelectedTextWithin(host);
  }

  protected onContextMenuOpen() {
    const host = this.messageTextRef?.nativeElement || document.getElementById(this.message.id);
    const selected = this.getSelectedTextWithin(host);
    this.selectedText = selected;
    this.initMenu(selected ? 'selected' : 'all'); // перебудувати меню під поточний стан
  }

  private getSelectedTextWithin(el?: HTMLElement | null): string {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return '';
    const text = sel.toString();
    if (!text.trim()) return '';
    if (!el) return text;

    const range = sel.getRangeAt(0);
    const isInside = el.contains(range.startContainer) && el.contains(range.endContainer);
    return isInside ? text : '';
  }

  private onCopySelected() {
    const selected = this.getSelectedTextWithin(this.messageTextRef?.nativeElement || null) || this.selectedText;
    if (selected && this.clipboard.copy(selected)) {
      this.notificationService.showInfo('', 'Виділений текст скопійовано');
    } else {
      this.notificationService.showWarning(
        this.translateService.instant('notifications_texts.warn.warn'),
        'Виділення відсутнє',
      );
    }
  }

  private onCopyAll() {
    const text = this.message.text || '';
    if (text && this.clipboard.copy(text)) {
      this.notificationService.showInfo(
        '',
        activeTranslations.notifications_texts.info.copied,
      );
    }
  }

  private onCopy() {
    const text = this.selectedText || this.message.text;
    if (text && this.clipboard.copy(text)) {
      this.notificationService.showInfo(
        '',
        activeTranslations.notifications_texts.info.copied,
      );
      // document.addEventListener('click', this.closeMenuOnOutsideClick.bind(this), true);
    }
  }

  private onCheckRead() {
    // document.addEventListener('click', this.closeMenuOnOutsideClick.bind(this), true);
    this.chatService.reloadMessage(this.chatService.selectedChatId, this.message.id).subscribe({
      next: (reloadedMessage: Message) => {
        const candidate = this.chatService.chatMessagesInfo.items.find((v: Message) => v.id === reloadedMessage.id);
        if (candidate) candidate.is_read = reloadedMessage.is_read;
        if (reloadedMessage.is_read) {
          this.initMenu();
          this.notificationService.showSuccess(
            '',
            activeTranslations.notifications_texts.success.message_read,
          );
        }
      },
      error: (res) => {
        (res.error?.errors || []).forEach((err: any) => {
          this.notificationService.showError('', ERROR_MESSAGES[err.code] || DEFAULT_ERROR);
        });
      },
    });
  }

  private onPin() {
    // document.addEventListener('click', this.closeMenuOnOutsideClick.bind(this), true);
    this.chatService.doPinnedMessage(this.message, !this.message.is_pinned).subscribe({
      next: (res) => {
        const candidate = this.chatService.chatMessagesInfo.items.find((v) => v.id === this.message.id);
        if (candidate) candidate.is_pinned = res.is_pinned;
        this.initMenu();
        this.chatService.getPinnedByChat();
      },
      error: (res) => {
        (res.error?.errors || []).forEach((err: any) => {
          this.notificationService.showError('', ERROR_MESSAGES[err.code] || DEFAULT_ERROR);
        });
      },
    });
  }

  private onResend() {
    this.onSendMessage.emit({ text: this.message.text, attachments: this.message.attachments });
  }

  private onEdit() {
    this.onEditMessage.emit(this.message);
  }

  protected onReplyMessage(send_action: SEND_ACTION_TYPE) {
    this.onReply.emit({
      message: this.message,
      send_action: send_action,
    });
  }

  protected reloadImages(image: Attachment) {
    image.loading = true;
    this.chatService.reloadMessage(this.message.chat_id, this.message.id).subscribe({
      next: (res: any) => {
        image.loading = false;
        this.message = res;
        this.initAttachments();
      },
      error: (res: any) => {
        image.loading = false;
        (res.error?.errors || []).forEach((err: any) => {
          this.notificationService.showError('', ERROR_MESSAGES[err.code] || DEFAULT_ERROR);
        });
      },
    });
  }

  protected loadError(event: any) {
    event.error = true;
  }

  protected goToMessage(): void {
    this.chatService.chatPinnedInfo.visible = false;
    setTimeout(() => {
      this.chatService.findMessage(this.message.id);
    }, 0);
  }

  protected onSaveQuickReply(): void {
    if (!this.message.text) return;

    this.messagesTemplatesService.save({ text: this.message.text.trim() }).subscribe({
      next: () => {
        this.notificationService.showSuccess(
          '',
          activeTranslations.notifications_texts.success.fast_answer_save,
        );
      },
      error: () => {
        this.notificationService.showError(
          '',
          activeTranslations.notifications_texts.error
            .there_was_an_error_please_try_again,
        );
      },
    });
  }

  protected onReschedule(): void {
    if (!this.message.x_data?.planner?.id) return;

    this.plannerActionService.updatePlanner({
      items: [this.message.x_data.planner],
      destroy$: this.destroy$,
      reloadTable: () => {
        this.chatService.getDelayedByChat();
      },
    });
  }

  protected onSendNow(): void {
    if (!this.message.x_data?.planner?.id) return;

    this.plannerActionService.runPlanners({
      items: [this.message.x_data.planner],
      destroy$: this.destroy$,
      reloadTable: () => {
        this.chatService.getDelayedByChat();
      },
      ...plannerExtraActionContextMap['runPlanners'],
    });
  }

  protected onDeleteDelayed(): void {
    if (!this.message.x_data?.planner?.id) return;

    this.plannerActionService.cancelPlanners({
      items: [this.message.x_data.planner],
      destroy$: this.destroy$,
      reloadTable: () => {
        this.chatService.getDelayedByChat();
      },
      ...plannerExtraActionContextMap['cancelPlanners'],
    });
  }

  protected onDeleteMessage(): void {
    this.chatService.deleteMessage(this.message.id).subscribe();
  }

  protected onOpenAttachment(attachment: Attachment) {
    const mimeType = attachment.mime_type || attachment.mimeType;
    const type = mimeType ? getFileViewerType(mimeType) : undefined;
    const url = type ? this.getAttachmentUrl(attachment) : undefined;

    if (!type || !url) {
      this.onDownloadAttachment(attachment);
      return;
    }

    this.modalService.openFileViewer(
      [
        {
          id: String(attachment.id || attachment.key || url),
          name: attachment.name,
          mimeType: mimeType!,
          type,
          url,
        },
      ],
      0,
    );
  }

  protected isViewableAttachment(attachment: Attachment): boolean {
    const mimeType = attachment.mime_type || attachment.mimeType;

    return !!mimeType && !!getFileViewerType(mimeType) && !!this.getAttachmentUrl(attachment);
  }

  private getAttachmentUrl(attachment: Attachment): string | undefined {
    if (attachment.url) return attachment.url;
    if (attachment.key) return `${environment.baseURL}/assets/download?key=${attachment.key}`;

    return attachment.shown_url;
  }

  protected onDownloadAttachment(attachment: Attachment) {
    if (!attachment.url && !attachment.id && !attachment.key) return;

    if (attachment.shown_url) {
      this.fileSaveService.saveFileFromUrlBlob(attachment.shown_url, attachment.name);
      return;
    }

    this.fetchAndPrepareAttachment(attachment, false, (blob) => {
      this.saveFileHandle(blob, attachment);
    });
  }

  private saveFileHandle = (blob: Blob, file: Attachment) => {
    file.loading = true;
    const notificationSuccess = activeTranslations.notifications_texts.success;

    this.fileSaveService.saveFile(blob, file.name);
    this.notificationService.showSuccess(notificationSuccess.success, notificationSuccess.successfully_downloaded);
    file.loading = false;
  };

  get messageClasses(): Record<string, boolean> {
    return {
      'message-received': !this.message.created_by,
      'message-sent': !!this.message.created_by,
      'message-transparent':
        (!!this.media?.length && !this.message.text && !this.isSharedPost && !this.productData) ||
        !!this.sticker ||
        !!this.round,
      'message-has-sticker': !!this.sticker,
      'message-has-sticker-not-load': !!this.sticker && (!this.sticker.shown_url || !!this.sticker.error),
      'message-has-round': !!this.round,
      'message-has-audio': !!this.audio?.length,
      'message-has-media': !!this.media?.length || !!this.commentedPostData?.attachments,
      'message-has-files': !!this.files?.length,
      'message-has-text': !!this.message.text || !!this.commentedPostData || this.isSharedPost || !!this.productData,
      'message-has-header':
        (this.chatService.chat.sub_type === CHAT_SUB_TYPES.GROUP && !this.message.created_by && !!this.sender?.name) ||
        !!this.forwardedFrom ||
        !!this.replyTo ||
        !!this.commentedPostData?.post_url ||
        this.isSharedPost ||
        !!this.productData,
      'message-deleted': !!this.message.deleted_at,
    };
  }

  protected getGridClass(media: any[]): string {
    const count = media.length;
    if (media.length < 3) return '';

    if (count === 3) return 'grid-2-first-2';
    if ((count - 1) % 3 === 0) return 'grid-3-first-3';
    if ((count - 2) % 3 === 0) return 'grid-3-first-two-3';
    if (count % 3 === 0) return 'grid-3';
    return '';
  }

  protected toggleLogs() {
    this.isLogsVisible = !this.isLogsVisible;
  }

  private mapCommentedInstPostToAttachments(sharedInstPost: InstSharedPost): Attachment[] {
    const attachments: Attachment[] = [];
    if (sharedInstPost.children?.data) {
      sharedInstPost.children.data.forEach((child: any) => {
        attachments.push({
          id: child.id,
          shown_url: child.media_url,
          type: ATTACHMENTS_TYPES[child.media_type as keyof typeof ATTACHMENTS_TYPES],
        } as Attachment);
      });
    } else {
      if (!sharedInstPost.media_url) {
        return attachments;
      }

      attachments.push({
        shown_url: sharedInstPost.media_url,
        type: ATTACHMENTS_TYPES[sharedInstPost.media_type as keyof typeof ATTACHMENTS_TYPES],
      } as Attachment);
    }

    return attachments;
  }

  protected goToReplyToMessage() {
    if (!this.replyTo) return;

    if (this.replyTo.is_another_chat && !this.replyTo.from_chat_id) {
      this.notificationService.showWarning(
        this.translateService.instant('notifications_texts.warn.warn'),
        this.translateService.instant('notifications_texts.warn.no_chat'),
      );
    }

    if (!this.replyTo.to_msg_id) {
      this.notificationService.showWarning(
        this.translateService.instant('notifications_texts.warn.warn'),
        this.translateService.instant('notifications_texts.warn.no_message'),
      );
    }

    if (this.replyTo.is_another_chat && this.replyTo.from_chat_id) {
      this.chatService.selectChat(this.replyTo.from_chat_id, this.replyTo.to_msg_id);
    } else if (this.replyTo?.to_msg_id) {
      this.chatService.findMessage(this.replyTo.to_msg_id);
    }
  }

  protected onChangeReaction(reaction: TReaction) {
    this.sendMessageReaction(this.message.id, this.message.chat_id, reaction);
  }

  public sendMessageReaction(message_id: string, chat_id: string, reaction: TReaction) {
    const requestBody: any = {
      message_id: message_id,
      chat_id: chat_id,
    };

    switch (this.chatService.chat.external_type) {
      case CHAT_EXTERNAL_TYPE.INSTAGRAM: {
        requestBody.reaction = this.getInstagramReaction(reaction);
        break;
      }
    }

    this.chatService.sendMessageReaction(requestBody).subscribe({
      next: () => {},
      error: (res: any) => {
        (res.error?.errors || []).forEach((err: any) => {
          this.notificationService.showError('', ERROR_MESSAGES[err.code] || DEFAULT_ERROR);
        });
      },
    });
  }

  private getInstagramReaction(reaction: TReaction): MessageReactions {
    const action = INST_REACTION_ACTION.REACT;

    if (this.message.x_data?.reaction?.reaction === reaction.code || !reaction.code) {
      return {
        action: INST_REACTION_ACTION.UNREACT,
      } as MessageReactions;
    }

    return {
      action: action,
      reaction: reaction.code,
      emoji: reaction.emoji,
      x_data: reaction.x_data,
    } as MessageReactions;
  }

  protected showChangeReaction(): boolean {
    const isInstagram = this.chatService.chat.external_type === CHAT_EXTERNAL_TYPE.INSTAGRAM;
    const isNotComment = getExternalIDPrefixType(this.message.external_id) !== EXTERNAL_ID_PREFIX.INSTAGRAM_COMMENT;

    return !this.message.created_by && isInstagram && isNotComment;
  }

  protected isShowMessageActionsTemplate(): boolean {
    const externalIdPrefix = getExternalIDPrefixType(this.chatService.chat.external_id);
    return (
      (externalIdPrefix === EXTERNAL_ID_PREFIX.INSTAGRAM_COMMENT ||
        externalIdPrefix === EXTERNAL_ID_PREFIX.FACEBOOK_COMMENT) &&
      !this.message.created_by
    );
  }

  protected missingCount(r: MessageReactionsEmoji) {
    const shown = Math.min(3, r.users?.length ?? 0);
    return Math.max(0, (r.count ?? 0) - shown);
  }

  protected readonly open = open;
  protected readonly BTN_ACTIONS = BTN_ACTIONS;
  protected readonly MESSAGE_TYPES = MESSAGE_TYPES;
  protected readonly riaAuctionMessageTypes = riaAuctionMessageTypes;
  protected readonly riaTradeMessageTypes = riaTradeMessageTypes;
  protected readonly riaTradeAcceptMessageTypes = riaTradeAcceptMessageTypes;
  protected readonly riaTradeDeclineMessageTypes = riaTradeDeclineMessageTypes;
  protected readonly riaTradePhotoMessageTypes = riaTradePhotoMessageTypes;
  protected readonly ATTACHMENTS_TYPES = ATTACHMENTS_TYPES;
  protected readonly attachmentsTooltipMap = attachmentsTooltipMap;
  protected readonly Math = Math;
  protected readonly CHAT_SUB_TYPES = CHAT_SUB_TYPES;
  protected readonly CHAT_EXTERNAL_TYPE = CHAT_EXTERNAL_TYPE;
  protected readonly SEND_ACTION_TYPE = SEND_ACTION_TYPE;
  protected readonly RELOAD_MEDIA_TYPE_CHATS = RELOAD_MEDIA_TYPE_CHATS;
  protected readonly AVATAR_REACTIONS_EXTERNAL_TYPES = AVATAR_REACTIONS_EXTERNAL_TYPES;
}
